import { ChangeDetectionStrategy, Component, Input, inject, input } from '@angular/core';

import { RATING_THUMB_OPTIONS } from '@core/evaluations-util';
import { I18nService } from '@core/i18n-legacy';
import { RatingLevel, type RatingLevelTranslations } from '@core/interview-results-types';
import { getRatingName, mapStringToRating } from '@core/interview-results-util';
import {
  ComponentVariantTransform,
  IconComponent,
  IconSizes,
  RtTooltip,
  TruncateComponent,
  type Variants,
} from '@core/user-interface-legacy';

const DEFAULT_RATING_LEVEL_TRANSLATIONS: RatingLevelTranslations = {
  [RatingLevel.StrongYes]: RATING_THUMB_OPTIONS[RatingLevel.StrongYes].translationKey,
  [RatingLevel.Yes]: RATING_THUMB_OPTIONS[RatingLevel.Yes].translationKey,
  [RatingLevel.Neutral]: RATING_THUMB_OPTIONS[RatingLevel.Neutral].translationKey,
  [RatingLevel.No]: RATING_THUMB_OPTIONS[RatingLevel.No].translationKey,
};

@Component({
  selector: 'rt-interview-evaluation-rating',
  templateUrl: 'interview-evaluation-rating.component.html',
  styleUrls: ['./interview-evaluation-rating.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'variant()',
  },
  imports: [RtTooltip, IconComponent, TruncateComponent],
})
export class InterviewEvaluationRatingComponent {
  public static readonly VARIANTS: Variants = [
    'default',
    ['default', 'hasLabel', 'other', 'no-padding'],
  ];

  private readonly i18n = inject(I18nService);

  protected readonly ratingThumbOptions = RATING_THUMB_OPTIONS;

  public readonly translations = input<RatingLevelTranslations>();

  public readonly variant = input(
    ...ComponentVariantTransform(InterviewEvaluationRatingComponent.VARIANTS),
  );
  public readonly size = input<keyof typeof IconSizes>('medium');
  public readonly centered = input<boolean>(true);
  public readonly tooltip = input<boolean>(true);
  public readonly colorize = input<boolean>(true);
  public readonly offer = input<{
    title: string;
  }>();

  @Input()
  public set rating(value: number | string | null) {
    if (typeof value === 'number') {
      this.ratingKey = getRatingName(value);
    } else {
      this.ratingKey = mapStringToRating(<RatingLevel>value);
    }
  }

  @Input() public stage: any;

  public ratingKey: RatingLevel;

  public hasVariant(variant: string): boolean {
    return (this.variant() || '').includes(variant);
  }

  public getRatingTranslation(ratingKey: RatingLevel): string {
    const translations = this.translations() || DEFAULT_RATING_LEVEL_TRANSLATIONS;
    return translations[ratingKey] ? this.i18n.translate(translations[ratingKey]) : '-';
  }
}
